import { ChangeEvent } from 'react';
import { Alert, AlertTitle } from '@mui/material';
import {
  CoreBox,
  CoreFormControl,
  CoreFormControlLabel,
  CoreRadio,
  CoreRadioGroup,
  CoreTextField,
  CoreTypography,
} from '@youscience/core';
import { CounselorRecommendationSchema, YesNo } from '@youscience/commonapp-service-common';
import { z } from 'zod';

import SectionAccordion from '@components/SectionAccordion';
import RichTextEditor from '@components/RichTextEditor';

import { FormState } from '@constants/commonApp/types';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

import { getFilename } from '@pages/StudentDetailsPage/components/common/formUtils';
import FileUpload from '@pages/StudentDetailsPage/components/common/FileUpload';

import { sxStyles, StyledListItem, StyledList } from '../../CounselorRecommendation.styles';

const NO_EVALUATION_WORDS_LENGTH = 250;
const EVALUATION2_WORDS_LENGTH = 1000;

export type WrittenEvaluationFields = Partial<z.infer<typeof CounselorRecommendationSchema>>;

interface WrittenEvaluationProps {
  writtenEvaluation?: WrittenEvaluationFields;
  onFormChange: (formState: FormState<WrittenEvaluationFields>) => void;
  onExpand: () => void;
  isExtended: boolean;
  isSaved: boolean;
  applicantName?: string;
  showError?: boolean;
}

const validator = CounselorRecommendationSchema;

export const WrittenEvaluation = ({
  writtenEvaluation = {},
  onFormChange,
  onExpand,
  isExtended,
  isSaved,
  applicantName = '',
  showError,
}: WrittenEvaluationProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<WrittenEvaluationFields, typeof validator>(
    writtenEvaluation,
    validator,
    isSaved,
    onFormChange,
  );

  const handleTextFieldChange =
    ({ optional = false } = {}) =>
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setFormStateWithErrors({ [event.target.name]: event.target.value || (optional ? null : undefined) });
    };

  const handleRichTextEditorChange =
    (name: string, optional = false) =>
    (content: string) => {
      setFormStateWithErrors({ [name]: content || (optional ? null : undefined) });
    };

  const handleRadioChange =
    ({ optional = false } = {}) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setFormStateWithErrors({ [event.target.name]: event.target.value || (optional ? null : undefined) });
    };

  const handleFileChange =
    ({ optional = false } = {}) =>
    (field: string) =>
    async (file: string | undefined) => {
      setFormStateWithErrors({ [field]: file || (optional ? null : undefined) });
    };

  return (
    <SectionAccordion
      title="Written evaluation"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={showError || !formState.isFormValid}
    >
      <CoreBox sx={sxStyles.accordionDetails}>
        <CoreTypography sx={sxStyles.infoText}>
          You can submit this form only one time for this applicant. Your responses and all accompanying documents will
          be shared with all of the colleges to which the applicant applies. For that reason, you should not tailor this
          form for any particular institution. Once you submit, you will be unable to modify these forms or documents in
          any way.
        </CoreTypography>

        <CoreTextField
          name="knowingApplicant"
          value={formState.data.knowingApplicant || ''}
          label="How long have you known this student and in what context?"
          multiline
          rows={3}
          error={!!formState.errors.knowingApplicant}
          helperText={formState.errors.knowingApplicant || `${formState.data?.knowingApplicant?.length || 0}/100`}
          inputProps={{ maxLength: 100 }}
          onChange={handleTextFieldChange({ optional: true })}
        />

        <CoreTextField
          name="studentDescription"
          value={formState.data.studentDescription || ''}
          label="What are the first words that come to your mind to describe this student?"
          multiline
          rows={3}
          error={!!formState.errors.studentDescription}
          helperText={formState.errors.studentDescription || `${formState.data?.studentDescription?.length || 0}/150`}
          inputProps={{ maxLength: 150 }}
          onChange={handleTextFieldChange({ optional: true })}
        />

        <CoreBox>
          <CoreTypography sx={sxStyles.infoText}>
            Please provide comments that will help us differentiate this student from others. We especially welcome a
            broad-based assessment and encourage you to consider describing or addressing:
          </CoreTypography>

          <StyledList>
            <StyledListItem>The applicant's academic, extracurricular, and personal characteristics.</StyledListItem>

            <StyledListItem>
              Relevant context for the applicant's performance and involvement, such as particularities of family
              situation or after-school obligations, either positive or negative.
            </StyledListItem>

            <StyledListItem>
              Observed problematic behaviors, perhaps separable from academic performance, that an admission committee
              should explore further.
            </StyledListItem>
          </StyledList>

          <FileUpload
            title={`Please upload your evaluation${!formState.data.noEvaluation ? ' *' : ''}`}
            name="evaluation"
            initialFile={formState.data.evaluation}
            previewTitle="Current Courses Transcript"
            filename={formState.data.evaluation ? getFilename(applicantName, 'evaluation') : undefined}
            onFileUpload={handleFileChange({ optional: true })}
            onFileDelete={handleFileChange({ optional: true })}
          />
        </CoreBox>

        <CoreTypography sx={sxStyles.infoText} variant="subtitle1" component="h2">
          OR
        </CoreTypography>
        {!formState.data.noEvaluation && !formState.data.evaluation && !formState.data.evaluation2 && (
          <CoreTypography color="error" variant="caption" sx={{ display: 'block', mt: 1, mb: 1 }}>
            An evaluation is required
          </CoreTypography>
        )}

        <RichTextEditor
          title={`Provide a short evaluation in this space${!formState.data.noEvaluation ? ' *' : ''}`}
          value={formState.data.evaluation2 || ''}
          wordsLimit={EVALUATION2_WORDS_LENGTH}
          onChange={handleRichTextEditorChange('evaluation2', true)}
        />

        <Alert data-testid="infoMessage" sx={sxStyles.alert} severity="info">
          <AlertTitle>If you will not be providing a personal written evaluation,</AlertTitle>
          please select the option below to inform our member institutions that you will not supply a written
          evaluation. Note you may substitute another school official's evaluation in place of one from you personally.
        </Alert>

        <CoreFormControl>
          <CoreTypography sx={sxStyles.infoText} variant="body2">
            Select your option
          </CoreTypography>

          <CoreRadioGroup
            value={formState.data.noEvaluation || ''}
            name="noEvaluation"
            onChange={handleRadioChange({ optional: true })}
          >
            <CoreFormControlLabel
              control={<CoreRadio value={''} checked={!formState.data.noEvaluation} />}
              label="Submit my evaluation"
            />

            <CoreFormControlLabel
              control={<CoreRadio value={YesNo.Yes} />}
              label="I will not be sending an evaluation for this student."
            />
          </CoreRadioGroup>

          <CoreTypography sx={sxStyles.infoText} variant="caption">
            Counselor evaluations often provide invaluable context to a student's application. We recognize, however,
            that you may not be able to provide one.
          </CoreTypography>
        </CoreFormControl>

        {formState.data.noEvaluation === YesNo.Yes ? (
          <RichTextEditor
            title="If you wish to provide additional details, you may do so here."
            value={formState.data.noEvaluationReason || ''}
            wordsLimit={NO_EVALUATION_WORDS_LENGTH}
            onChange={handleRichTextEditorChange('noEvaluationReason', true)}
          />
        ) : null}
      </CoreBox>
    </SectionAccordion>
  );
};
